import { X } from "lucide-react";
import { useEffect, useState, type CSSProperties, type ComponentProps } from "react";
import { Toaster as Sonner, toast } from "sonner";

type ToasterProps = ComponentProps<typeof Sonner>;

/** Tema atual pela classe `dark` do `<html>`, que o `aplicarTema` liga e desliga. */
function useTemaDoDocumento(): "light" | "dark" {
  const ler = () => (document.documentElement.classList.contains("dark") ? "dark" : "light");
  const [tema, setTema] = useState<"light" | "dark">(ler);
  useEffect(() => {
    const observador = new MutationObserver(() => setTema(ler()));
    observador.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observador.disconnect();
  }, []);
  return tema;
}

/**
 * Base de todo toast: cartao de 420px, canto xl, texto 14/21, fechar a direita.
 * `select-none`: o sonner ignora o arraste para fechar quando ha texto
 * selecionado, e arrastar sobre o texto selecionaria antes de deslizar.
 */
const BASE =
  "flex w-full cursor-grab items-center gap-3 rounded-xl p-4 text-sm leading-[21px] shadow-lg ring-1 ring-border/60 select-none active:cursor-grabbing";

/** Icone do aviso num selo ambar suave, do tamanho do botao de fechar. */
const SELO_DE_AVISO =
  "[&_[data-icon]]:grid [&_[data-icon]]:size-8 [&_[data-icon]]:place-items-center [&_[data-icon]]:rounded-lg [&_[data-icon]]:bg-warning/15 [&_[data-icon]]:text-warning";

/**
 * Toaster do shadcn (sonner) no formato do painel: pilha no canto inferior
 * direito (fechada mostra 3; no hover abre e lista ate 8, e pausa o tempo; o
 * corte dos 3 fica em `tokens.css`), 3 s na tela, cor solida
 * por tipo e o X a direita. Aviso fica no cartao, com selo ambar no icone. Sem `next-themes`: o tema vem da classe `dark` do
 * documento. Cores so por token, entao o tema escuro sai de graca.
 * Monte uma vez na raiz do app e dispare com `toast(...)`, `toast.success(...)`.
 */
function Toaster({ style, ...props }: ToasterProps) {
  const tema = useTemaDoDocumento();
  return (
    <Sonner
      theme={tema}
      className="toaster group"
      position="bottom-right"
      duration={3000}
      gap={10}
      visibleToasts={8}
      closeButton
      icons={{ close: <X className="size-4" aria-hidden="true" /> }}
      containerAriaLabel="Notificações"
      style={{ "--width": "420px", ...style } as CSSProperties}
      toastOptions={{
        unstyled: true,
        closeButtonAriaLabel: "Fechar notificação",
        classNames: {
          toast: BASE,
          default: "bg-card text-card-foreground",
          info: "bg-card text-card-foreground",
          loading: "bg-card text-card-foreground",
          success: "bg-primary text-primary-foreground",
          error: "bg-destructive text-destructive-foreground",
          // Aviso no cartao, como o resto do painel: o ambar fica so no selo do
          // icone e a acao vai na cor primaria, antes do X, em vez de pintar o toast inteiro.
          warning: `bg-card text-card-foreground ${SELO_DE_AVISO} [&_[data-action]]:order-9 [&_[data-action]]:bg-primary [&_[data-action]]:text-primary-foreground`,
          icon: "shrink-0 [&_svg]:size-4",
          content: "flex min-w-0 flex-1 flex-col gap-0.5",
          title: "font-medium",
          description: "opacity-80",
          closeButton:
            "order-last grid size-8 shrink-0 cursor-pointer place-items-center rounded-md text-current transition-colors hover:bg-current/10 focus-visible:outline-2 focus-visible:outline-offset-2",
          actionButton:
            "order-last h-8 shrink-0 cursor-pointer rounded-md bg-foreground px-3 text-xs font-medium text-background transition-opacity hover:opacity-90",
          cancelButton:
            "order-last h-8 shrink-0 cursor-pointer rounded-md px-3 text-xs font-medium text-current transition-colors hover:bg-current/10",
        },
      }}
      {...props}
    />
  );
}

export { Toaster, toast };
